import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { IconButton, Stack, TextField } from "@mui/material";
import { MicNoneRounded, SendRounded } from "@mui/icons-material";
import { enqueueSnackbar } from "notistack";
import { useLocation } from "react-router-dom";
import { useCase } from "../../provider/CaseProvider";
import { useAiChat } from "../../provider/AiChatProvider";
import { useAiEventStreamControls } from "../../hooks/useAiEventStream";
import { createResource } from "../../services/ReactQuery/reactQueryService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sendAiChatPrompt, transcribeAiChatVoiceRecording } from "../../services/aiChatService";
import { useVoiceRecording, VoiceFinishMode, VoiceRecordingStatus } from "../../hooks/useVoiceRecording";
import VoiceRecordingBar from "./VoiceRecordingBar";
import { insertTranscriptIntoText, SavedInputState } from "./voiceTranscriptInsertion";
import {
  canCancelVoiceRecording,
  isVoiceContextCurrent,
  VoiceContext,
} from "./voiceRecordingState";

type AIChatInputProps = {
  isPanelExpanded: boolean;
};

type VoiceInputSnapshot = SavedInputState & VoiceContext;

const VOICE_BAR_STATUSES: VoiceRecordingStatus[] = ["recording", "transcribing", "sending"];

export default function AIChatInput({ isPanelExpanded }: AIChatInputProps) {
  const [prompt, setPrompt] = useState("");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const savedInputRef = useRef<VoiceInputSnapshot | null>(null);

  const { product } = useCase();
  const { activeThreadId, setActiveThreadId, setIsLoading, isLoading, activeAgent, activeEditStepId } = useAiChat();
  const { connect, reset } = useAiEventStreamControls();
  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const location = useLocation();
  const currentVoiceContext: VoiceContext = {
    backofficeCaseId: product?.backofficeCase.id ?? null,
    activeThreadId,
    activeAgent,
    activeEditStepId,
    locationKey: location.key,
    isPanelExpanded,
  };
  const currentVoiceContextRef = useRef(currentVoiceContext);
  currentVoiceContextRef.current = currentVoiceContext;

  const restoreSavedSelection = () => {
    const saved = savedInputRef.current;
    if (!saved) {
      return;
    }
    setPrompt(saved.text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(saved.selectionStart, saved.selectionEnd);
    });
  };

  const restorePromptWithTranscript = (saved: VoiceInputSnapshot, transcript: string) => {
    const { text, cursorPosition } = insertTranscriptIntoText(saved, transcript);
    setPrompt(text);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(cursorPosition, cursorPosition);
    });
  };

  const sendTranscriptAsMessage = async (saved: VoiceInputSnapshot, transcript: string) => {
    if (!product || !isVoiceContextCurrent(saved, currentVoiceContextRef.current)) {
      return;
    }

    let createdThreadId: number | null = null;
    const captureCreatedThreadId: Dispatch<SetStateAction<number | null>> = (nextThreadId) => {
      if (typeof nextThreadId !== "function") {
        createdThreadId = nextThreadId;
        if (isVoiceContextCurrent(saved, currentVoiceContextRef.current)) {
          setActiveThreadId(nextThreadId);
        }
        return;
      }

      if (isVoiceContextCurrent(saved, currentVoiceContextRef.current)) {
        setActiveThreadId(nextThreadId);
      }
    };

    try {
      await sendAiChatPrompt(
        product,
        saved.activeThreadId,
        captureCreatedThreadId,
        createMutation,
        queryClient,
        connect,
        reset,
        transcript,
        setIsLoading,
        saved.activeAgent,
        saved.activeEditStepId,
      );
    } catch {
      if (!isVoiceContextCurrent(saved, currentVoiceContextRef.current, createdThreadId ?? saved.activeThreadId)) {
        return;
      }
      restorePromptWithTranscript(saved, transcript);
      enqueueSnackbar("Die Nachricht konnte nicht gesendet werden.", { variant: "error" });
    }
  };

  const voice = useVoiceRecording({
    transcribe: async (audio) => (await transcribeAiChatVoiceRecording(audio)).transcript,
    onFinished: async (transcript, mode) => {
      const saved = savedInputRef.current;
      if (!saved || !isVoiceContextCurrent(saved, currentVoiceContextRef.current)) {
        return;
      }

      if (mode === "send" && saved.text === "") {
        await sendTranscriptAsMessage(saved, transcript);
        return;
      }

      restorePromptWithTranscript(saved, transcript);
    },
    onError: (message) => {
      restoreSavedSelection();
      enqueueSnackbar(message, { variant: "error" });
    },
  });

  const cancelVoice = voice.cancel;
  useEffect(() => {
    cancelVoice();
  }, [
    cancelVoice,
    currentVoiceContext.backofficeCaseId,
    currentVoiceContext.activeThreadId,
    currentVoiceContext.activeAgent,
    currentVoiceContext.activeEditStepId,
    currentVoiceContext.locationKey,
    currentVoiceContext.isPanelExpanded,
  ]);

  if (!product) {
    return null;
  }

  const sendCurrentPrompt = async () => {
    await sendAiChatPrompt(
      product,
      activeThreadId,
      setActiveThreadId,
      createMutation,
      queryClient,
      connect,
      reset,
      prompt,
      setIsLoading,
      activeAgent,
      activeEditStepId,
    );
    setPrompt("");
  };

  const startVoiceRecording = async () => {
    const input = inputRef.current;
    savedInputRef.current = {
      text: prompt,
      selectionStart: input?.selectionStart ?? prompt.length,
      selectionEnd: input?.selectionEnd ?? prompt.length,
      backofficeCaseId: product.backofficeCase.id,
      activeThreadId,
      activeAgent,
      activeEditStepId,
      locationKey: location.key,
      isPanelExpanded,
    };
    await voice.start();
  };

  const cancelAndRestoreInput = () => {
    voice.cancel();
    restoreSavedSelection();
  };

  const finishModeForArrowButton = (): VoiceFinishMode => (savedInputRef.current?.text === "" ? "send" : "insert");

  const showVoiceBar = VOICE_BAR_STATUSES.includes(voice.status);
  if (showVoiceBar) {
    const arrowWillSend = finishModeForArrowButton() === "send";
    return (
      <VoiceRecordingBar
        isTranscribing={voice.status !== "recording"}
        canCancel={canCancelVoiceRecording(voice.status)}
        statusMessage={voice.status === "sending" ? "Nachricht wird gesendet …" : "Wird transkribiert …"}
        arrowWillSend={arrowWillSend}
        getAudioLevel={voice.getAudioLevel}
        onCancel={cancelAndRestoreInput}
        onStop={() => voice.stop("insert")}
        onSend={() => voice.stop(finishModeForArrowButton())}
      />
    );
  }

  const isRequestingPermission = voice.status === "requesting_permission";

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();
        await sendCurrentPrompt();
      }}
    >
      <Stack direction={"row"} spacing={1} sx={{ padding: "1rem", alignItems: "center" }}>
        <TextField
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          fullWidth
          multiline
          disabled={isLoading || isRequestingPermission || !isPanelExpanded}
          inputRef={inputRef}
          maxRows={15}
          placeholder={`Frage ${activeAgent} zur Akte oder gib Anweisungen...`}
          onKeyDown={async (event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault();
              await sendCurrentPrompt();
            }
          }}
        />
        <IconButton
          onClick={startVoiceRecording}
          disabled={isLoading || isRequestingPermission || !isPanelExpanded}
          aria-label={"Diktat starten"}
          sx={{ minHeight: "2.75rem", minWidth: "2.75rem" }}
        >
          <MicNoneRounded />
        </IconButton>
        <IconButton
          type={"submit"}
          disabled={isLoading || isRequestingPermission || !isPanelExpanded}
          aria-label={"Nachricht senden"}
          sx={{ minHeight: "2.75rem", minWidth: "2.75rem" }}
        >
          <SendRounded />
        </IconButton>
      </Stack>
    </form>
  );
}
